/** @jsx jsx */
import React, {
	type KeyboardEvent,
	type FC,
	type MouseEvent,
	useCallback,
	useRef,
	useState,
	useContext,
	useMemo,
	Fragment,
	type PropsWithChildren,
} from 'react';
import { styled, jsx, css } from '@compiled/react';
import { useAnalyticsEvents, type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import DropdownMenu, { DropdownItemGroup, DropdownItem } from '@atlaskit/dropdown-menu';
import { Section } from '@atlaskit/menu';
import { Box, Text, xcss } from '@atlaskit/primitives';
import InteractionContext from '@atlaskit/react-ufo/interaction-context';
import { token } from '@atlaskit/tokens';
import { metrics } from '@atlassian/browser-metrics';
import { standardizeLocale } from '@atlassian/jira-common-constants/src/supported-locales.tsx';
import AsyncCreateDashboardGadgetModal from '@atlassian/jira-dashboard-create-dashboard-gadget-modal/src/async.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { ErrorState } from '@atlassian/jira-issue-navigator-actions-common/src/ui/error/index.tsx';
import { FilterButton } from '@atlassian/jira-issue-navigator-actions-common/src/ui/filter-button/index.tsx';
import {
	isCustomFilter,
	isFilterId,
} from '@atlassian/jira-issue-navigator-actions-common/src/utils/filters/index.tsx';
import { fireUIAnalytics, ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import {
	isAsyncExportItem,
	type AsyncExportIssuesCommonInput,
	type ExportItemType,
} from '../common/types.tsx';
import {
	appendExportIssuesRelPolicy,
	getCreateDashboardGadgetModalParams,
	useCreateDashboardGadgetModalControls,
} from '../common/utils.tsx';
import { ExportIssuesState } from '../controllers/export-issues-state/index.tsx';
import { CREATE_DASHBOARD_GADGET } from '../services/export-issues/constants.tsx';
import { useFetchAllExportIssuesResources } from '../services/export-issues/index.tsx';
import { ECOSYSTEM_APPS_GROUP_INDEX } from './constants.tsx';
import { ExportItem } from './export-item/index.tsx';
import messages from './messages.tsx';
import type { ExportIssuesProps } from './types.tsx';
import { getExportItems, sortConnectAppsInExportItems } from './utils.tsx';

const MaybeExportIssuesState = componentWithFG(
	'nin-export-issues-with-subscription',
	ExportIssuesState,
	Fragment,
);

const menuItemStyles = css({
	paddingTop: token('space.075'),
	paddingBottom: token('space.075'),
	paddingLeft: token('space.050'),
	paddingRight: token('space.050'),
});

export const ExportIssues: FC<ExportIssuesProps> = ({
	showEcosystemApps = true,
	browserMetricsKey = 'unknown',
	jql,
	filterId,
	isModifiedFilter = false,
	filterName,
	customTriggerButton,
	placement,
}: ExportIssuesProps) => {
	// This should be updated in the future when we're managing filter state more explicitly.
	const hasCustomFilterJqlBeenModified = isCustomFilter(filterId || '') && isModifiedFilter;

	const { fetch, exportItems, loading, error } = useFetchAllExportIssuesResources(
		jql,
		filterId,
		hasCustomFilterJqlBeenModified,
	);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const [openExportIssuesInteraction] = useState(
		metrics.interaction({
			key: `${browserMetricsKey}.load-export-issues-dropdown.start-end.diff`,
		}),
	);
	const { locale } = useTenantContext();
	const { current: intlCollator } = useRef(new Intl.Collator(standardizeLocale(locale)));
	const filteredExportItems = getExportItems(exportItems) || [];
	// Alphabetically sort the connect apps in the export issues dropdown.
	const orderedExportItems =
		(showEcosystemApps && sortConnectAppsInExportItems(filteredExportItems, intlCollator)) ||
		filteredExportItems;
	/** We only want to count the number of apps added to the exports dropdown */
	const appCount = orderedExportItems[ECOSYSTEM_APPS_GROUP_INDEX]?.length ?? 0;

	const {
		isOpen: isCreateDashboardGadgetModalOpen,
		openModal: openCreateDashboardGadgetModal,
		closeModal: closeCreateDashboardGadgetModal,
		clearAndNavigateToDashboard,
	} = useCreateDashboardGadgetModalControls();

	const dashboardGadgetModalContext = useContext(InteractionContext);

	const dashboardGadgetModalInstrumentedHandler = useCallback(
		(event: MouseEvent | KeyboardEvent) => {
			if (dashboardGadgetModalContext && dashboardGadgetModalContext.tracePress) {
				dashboardGadgetModalContext.tracePress('create-dashboard-gadget-modal', event.timeStamp);
			}
		},
		[dashboardGadgetModalContext],
	);

	// remove onClickDropDownItem when cleanup nin-export-issues-with-subscription fg
	const onClickDropDownItem = useCallback(
		(item: ExportItemType) => (e: MouseEvent | KeyboardEvent) => {
			if (item && item.id === CREATE_DASHBOARD_GADGET) {
				dashboardGadgetModalInstrumentedHandler(e);
				openCreateDashboardGadgetModal();
			}

			const analyticsEvent: UIAnalyticsEvent = createAnalyticsEvent({
				action: 'clicked',
			});
			fireUIAnalytics(analyticsEvent, 'dropdownItem clicked', 'export', {
				dropdownItem: item.id ?? 'app',
				dropdownItemName: item.label,
				appCount,
			});
		},
		[
			createAnalyticsEvent,
			appCount,
			dashboardGadgetModalInstrumentedHandler,
			openCreateDashboardGadgetModal,
		],
	);

	const onOpen = () => {
		/* We don't want to refetch if the last used jql is
		 * the same as the active jql.
		 */
		if (!isOpen) {
			openExportIssuesInteraction.start();
			fetch(false);
		}
		setIsOpen(!isOpen);
	};

	!loading && openExportIssuesInteraction.stop();

	const exportOptionHeaderLabel = exportItems?.viewOptionHeader?.label;

	let commonInput: AsyncExportIssuesCommonInput;
	if (fg('nin-export-issues-with-subscription')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		commonInput = useMemo<AsyncExportIssuesCommonInput>(() => {
			const validFilterId = isFilterId(filterId);

			return {
				jql,
				modified: isModifiedFilter,
				...(validFilterId && { filterId }),
			};
		}, [filterId, isModifiedFilter, jql]);
	}

	const isVREnabled = isVisualRefreshEnabled() && fg('visual-refresh-headers-buttons');

	const isHierarchyEnabled =
		// eslint-disable-next-line jira/ff/no-preconditioning
		expVal('jira_spreadsheet_component_m1', 'isHierarchyEnabled', false) &&
		fg('jsc_m2_hierarchy_fe_changes');

	return (
		<MaybeExportIssuesState scope="ExportIssuesState">
			<DropdownMenu
				{...(!isVREnabled &&
					fg('render_export_issues_menu_to_parent') && { shouldRenderToParent: true })}
				isLoading={loading}
				onOpenChange={onOpen}
				testId="issue-navigator-action-export-issues.ui.filter-button"
				trigger={({ triggerRef, ...props }) =>
					customTriggerButton ? (
						customTriggerButton(isOpen, { triggerRef, ...props })
					) : (
						<FilterButton
							customTriggerProps={{
								...props,
								ref: triggerRef,
							}}
							text={formatMessage(messages.exportButtonText)}
							label=""
							isSelected={isOpen}
							vcId="export-issues"
						/>
					)
				}
				shouldFlip
				placement={placement || 'bottom-end'}
				spacing="compact"
			>
				{!error && exportOptionHeaderLabel ? (
					<>
						<Section testId="issue-navigator-action-export-issues.ui.nso-bulk-export-info">
							<Box paddingInline="space.150" paddingBlock="space.100" xcss={styles}>
								<Text color="color.text.subtle" size="small">
									{exportOptionHeaderLabel}
								</Text>
							</Box>
						</Section>
						<Section hasSeparator>{null}</Section>
					</>
				) : null}
				{!error ? (
					orderedExportItems.map((group, index) =>
						showEcosystemApps || index !== ECOSYSTEM_APPS_GROUP_INDEX ? (
							<StyledDropDownItemGroup key={`export-item-group-${index}`}>
								<DropdownItemGroup>
									{group.map((option, dropdownItemIndex) =>
										fg('nin-export-issues-with-subscription') ? (
											<ContextualAnalyticsData
												key={
													(index === ECOSYSTEM_APPS_GROUP_INDEX &&
														`export-item-app-${dropdownItemIndex}`) ||
													option.id
												}
												attributes={{
													exportId: option.id,
													exportLabel: option.label,
													...(isAsyncExportItem(option)
														? {
																exportType: option.exportType,
																maxResults: option.maxResults,
																fileExtension: option.fileExtension,
															}
														: {}),
												}}
											>
												<ExportItem
													exportItem={option}
													appCount={appCount}
													commonInput={commonInput}
													onOpenCreateDashboardGadgetModal={openCreateDashboardGadgetModal}
												/>
											</ContextualAnalyticsData>
										) : (
											<DropdownItem
												key={
													(index === ECOSYSTEM_APPS_GROUP_INDEX &&
														`export-item-app-${dropdownItemIndex}`) ||
													option.id
												}
												onClick={onClickDropDownItem(option)}
												{...(option.id !== CREATE_DASHBOARD_GADGET && {
													href: option.link ?? '#',
													target: '_blank',
													rel: appendExportIssuesRelPolicy(option.rel),
												})}
											>
												{isVREnabled && isHierarchyEnabled ? (
													<div
														css={menuItemStyles}
														data-testid="issue-navigator-action-export-issues.ui.span"
													>
														{option.label}
													</div>
												) : (
													<span data-testid="issue-navigator-action-export-issues.ui.span">
														{option.label}
													</span>
												)}
											</DropdownItem>
										),
									)}
								</DropdownItemGroup>
							</StyledDropDownItemGroup>
						) : null,
					)
				) : (
					<ErrorState onRetry={() => fetch(true)} pageAction="exportIssues" />
				)}
			</DropdownMenu>
			{isCreateDashboardGadgetModalOpen ? (
				<AsyncCreateDashboardGadgetModal
					{...getCreateDashboardGadgetModalParams(
						jql,
						filterId,
						isCustomFilter,
						hasCustomFilterJqlBeenModified,
					)}
					onCancel={closeCreateDashboardGadgetModal}
					onSuccess={clearAndNavigateToDashboard}
					filterName={filterName}
				/>
			) : null}
		</MaybeExportIssuesState>
	);
};

export default ExportIssues;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledDropDownItemGroupOld = styled.div({
	marginTop: token('space.200'),
});
const styles = xcss({ maxWidth: '14rem' });

const dividerStyles = xcss({
	borderBottomStyle: 'solid',
	borderBottomWidth: 'border.width',
	borderBottomColor: 'color.border',
});

const StyledDropDownItemGroup = componentWithCondition(
	() => isVisualRefreshEnabled() && fg('visual-refresh-headers-buttons'),
	({ children, key }: PropsWithChildren<{ key: string }>) => (
		<Box key={key} xcss={dividerStyles}>
			{children}
		</Box>
	),
	StyledDropDownItemGroupOld,
);
